import React, { useEffect, useState } from "react";
import { postUserIntigration } from "../services/automationService";
import { getUserProfile } from "../services/authServices";
import { Button } from "antd";
import logger, { Logger } from '../utils/logger';
import { trackEvent } from "../config/firebase";

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

let FBApp2: any;
declare var FB: any;

interface IProps {
  onSuccess?: () => void;
}

const FacebookIntegration = ({ onSuccess }: IProps) => {
  // State management for component
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Handles the user profile fetching process after successful Facebook authentication
   * @param value - Facebook authentication response object
   */
  const fetchUserProfile = async (value: any) => {
    logger.log("Starting fetchUserProfile process with value:", value);
    setIsLoading(true);

    // 📊 Track when user returns from Facebook and we start finishing integration
    trackEvent("whatsapp_fb_callback_received", {
      platform: "web",
      timestamp: new Date().toISOString(),
      stage: "returned_from_facebook",
      has_auth_data: !!value,
    });

    try {
      // Prepare integration object for WhatsApp Business API
      const integrationObject = {
        integrationKey: "whatsapp_business",
        details: value,
      };

      logger.log("Posting user integration with object:", integrationObject);

      // 📊 Track API call to finish integration
      trackEvent("whatsapp_finish_integration_api_called", {
        platform: "web",
        timestamp: new Date().toISOString(),
        stage: "posting_integration_details",
        integration_key: "whatsapp_business",
      });

      // Post integration details to backend
      const integrationResult = await postUserIntigration(integrationObject);
      logger.log("Integration result received:", integrationResult);

      // 📊 Track API call success
      trackEvent("whatsapp_finish_integration_api_success", {
        platform: "web",
        timestamp: new Date().toISOString(),
        stage: "integration_api_completed",
        success: true,
        has_response: !!integrationResult.data,
      });

      // Fetch updated user profile
      const { data: userProfileData } = await getUserProfile();
      logger.log("User profile fetched successfully:", userProfileData);

      // Update component state with success message
      setMessage(integrationResult.data.message);

      // Store user data in localStorage for persistence
      localStorage.setItem("userData", JSON.stringify(userProfileData.data));
      logger.log("User data stored in localStorage");

      // 📊 Track successful WhatsApp integration completion
      trackEvent("whatsapp_integration_completed", {
        platform: "web",
        timestamp: new Date().toISOString(),
        success: true,
        user_has_profile: !!userProfileData.data,
        integration_message: integrationResult.data.message,
      });

      // Call success callback if provided
      onSuccess && onSuccess();

    } catch (error) {
      console.error("Error in fetchUserProfile process:", error);

      // 📊 Track integration profile fetch error
      trackEvent("whatsapp_integration_error", {
        platform: "web",
        timestamp: new Date().toISOString(),
        stage: "fetch_user_profile",
        error_message: error instanceof Error ? error.message : "Unknown error",
      });

      setError(
        "An error occurred while setting up your WhatsApp Business API integration. Please try again or contact support if the issue persists."
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refreshes user profile data and updates local storage
   */
  const onRefresh = async () => {
    logger.log("Refreshing user profile data");
    setIsLoading(true);
    
    try {
      const { data: userProfileData } = await getUserProfile();
      logger.log("Profile refresh successful:", userProfileData);
      
      // Update localStorage with fresh data
      localStorage.setItem("userData", JSON.stringify(userProfileData.data));
      
      // Call success callback
      onSuccess && onSuccess();
      
    } catch (error) {
      console.error("Error refreshing user profile:", error);
      setError("Failed to refresh profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Loads Facebook SDK and initializes FB object
   * This effect runs once when component mounts
   */
  useEffect(() => {
    const loadFBApp2 = () => {
      logger.log("Loading Facebook SDK");
      
      return new Promise<void>((resolve) => {
        // Create script element for Facebook SDK
        const script = document.createElement("script");
        script.src = "https://connect.facebook.net/en_US/sdk.js";
        script.async = true;
        
        // Handle successful SDK load
        script.onload = () => {
          logger.log("Facebook SDK loaded successfully");
          
          // Initialize Facebook SDK with app configuration
          FB.init({
            appId: "883658973014756", // 3Sigma CRM Facebook App ID
            cookie: true,
            xfbml: true,
            version: "v18.0",
          });
          
          // Store FB reference globally
          FBApp2 = FB;
          logger.log("Facebook SDK initialized");
          resolve();
        };
        
        // Add script to document body
        document.body.appendChild(script);
      });
    };

    // Load Facebook SDK on component mount
    loadFBApp2();
  }, []);

  /**
   * Initiates the WhatsApp Business API signup process through Facebook
   * This function handles the complete flow from integration post to Facebook login
   */
  const launchWhatsAppSignup = async () => {
    logger.log("Launching WhatsApp Business API signup process");
    setIsLoading(true);
    setError(null); // Clear any previous errors

    // 📊 Track button click - User initiates FB integration
    trackEvent("whatsapp_fb_login_button_clicked", {
      platform: "web",
      timestamp: new Date().toISOString(),
      source: "facebook_integration_page",
      action: "initiate_facebook_login",
    });

    // 📊 Track WhatsApp integration start
    trackEvent("whatsapp_integration_started", {
      platform: "web",
      timestamp: new Date().toISOString(),
      source: "profile_page",
      integration_type: "whatsapp_business",
    });

    try {
      // Post initial integration request to backend
      logger.log("Posting initial integration request");
      await postUserIntigration({
        integrationKey: "whatsapp_business",
      });

      logger.log("Starting Facebook login process");
      
      // Launch Facebook login with WhatsApp Business API specific configuration
      FBApp2.login(
        function (response: any) {
          logger.log("Facebook login response received:", response);
          
          if (response.authResponse) {
            Logger.log("Facebook login successful:", response);
            logger.log("Auth response received, processing user profile");

            // 📊 Track Facebook auth success
            trackEvent("whatsapp_facebook_auth_success", {
              platform: "web",
              timestamp: new Date().toISOString(),
              has_auth_response: true,
            });

            // Process the authentication response
            fetchUserProfile(response.authResponse);

          } else {
            logger.log("Facebook login cancelled or not authorized");
            Logger.log("User cancelled login or did not fully authorize.");

            // 📊 Track Facebook auth cancellation
            trackEvent("whatsapp_integration_cancelled", {
              platform: "web",
              timestamp: new Date().toISOString(),
              reason: "facebook_auth_cancelled",
              stage: "facebook_login",
            });

            setError(
              "Facebook login was cancelled or not fully authorized. Please try again to complete your WhatsApp Business API setup."
            );
            setIsLoading(false);
          }
        },
        {
          // WhatsApp Business API specific configuration
          config_id: "1066369917929689", // WABA configuration ID
          response_type: "code",
          override_default_response_type: true,
        }
      );
      
    } catch (error) {
      console.error("Error launching WhatsApp signup:", error);

      // 📊 Track integration error
      trackEvent("whatsapp_integration_error", {
        platform: "web",
        timestamp: new Date().toISOString(),
        stage: "launch_signup",
        error_message: error instanceof Error ? error.message : "Unknown error",
      });

      setError(
        "An error occurred while launching WhatsApp Business API signup. Please try again or contact support if the issue persists."
      );
      setIsLoading(false);
    }
  };

  /**
   * Scrolls page to top with smooth animation
   */
  const scrollToTop = () => {
    logger.log("Scrolling to top");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /**
   * Renders a card component with title and content
   * @param title - Card title
   * @param content - Card content (React node)
   * @returns JSX card element
   */
  const renderCard = (title: string, content: React.ReactNode) => (
    <div className="card">
      <h2 className="card-title">{title}</h2>
      {content}
    </div>
  );

  /**
   * Renders the WhatsApp API benefits and features section
   * Updated with latest feature descriptions and pricing info
   */
  const renderWhatsAppAPIBenefits = () => (
    <div>
      <p
        style={{
          fontSize: "16px",
          lineHeight: "1.7",
          color: "#555",
          marginBottom: "24px",
          textAlign: "justify",
        }}
      >
        The WhatsApp Business API integration in 3Sigma CRM empowers you to automate and
        streamline your business communications, helping you reach your
        customers quickly and effectively. This feature offers a range of
        powerful tools designed to enhance your messaging experience:
      </p>
      
      {/* Features list with icons and descriptions */}
      <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
        {[
          {
            icon: "📢",
            title: "Send Campaigns",
            description:
              "Launch targeted WhatsApp campaigns to promote your products, services, or special offers. Schedule campaigns in advance and track their performance to optimize your reach.",
          },
          {
            icon: "💬",
            title: "Automated Drip Messaging",
            description:
              "Set up automated drip message sequences that nurture your leads over time, keeping them engaged at every stage of the sales process.",
          },
          {
            icon: "⚡",
            title: "Trigger Automated Messages",
            description:
              "Use triggers based on customer actions (e.g., new lead entries, missed calls, or completed forms) to automatically send personalized messages, ensuring quick and effective follow-ups.",
          },
          {
            icon: "📥",
            title: "Manage a Shared Inbox",
            description:
              "Collaborate with your team using a shared WhatsApp inbox where you can assign messages, respond to customer queries, and track conversations in real time—all from a single dashboard.",
          },
          {
            icon: "📋",
            title: "Create and Manage Templates",
            description:
              "Design and save message templates for common interactions, such as appointment reminders, sales follow-ups, or welcome messages. With templates, you can maintain consistency and save time when messaging clients.",
          },
        ].map((item, index) => (
          <li
            key={index}
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "20px",
              padding: "16px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #e9ecef",
            }}
          >
            <span style={{ 
              fontSize: "28px", 
              marginRight: "16px",
              marginTop: "4px",
              minWidth: "40px"
            }}>
              {item.icon}
            </span>
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                  color: "#333",
                  lineHeight: "1.3",
                }}
              >
                {item.title}
              </h3>
              <p
                style={{
                  fontSize: "15px",
                  lineHeight: "1.6",
                  color: "#555",
                  margin: 0,
                  textAlign: "justify",
                }}
              >
                {item.description}
              </p>
            </div>
          </li>
        ))}
      </ul>
      
      {/* Pricing information section */}
      <div
        style={{
          marginTop: "24px",
          padding: "20px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "12px",
          color: "white",
          textAlign: "center",
        }}
      >
        <h3
          style={{
            fontSize: "20px",
            fontWeight: "bold",
            marginBottom: "12px",
            color: "white",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
          }}
        >
          💰 How Users Are Charged
        </h3>
        <p
          style={{
            fontSize: "16px",
            lineHeight: "1.6",
            color: "white",
            margin: 0,
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        >
          The WhatsApp Business API feature is billed based on message volume and usage.
          Charges apply per message sent, including campaigns, automated
          sequences, and template messages. We offer flexible plans tailored to
          your needs, with all charges added to your monthly invoice for easy
          monitoring.
        </p>
      </div>
    </div>
  );

  /**
   * Renders the updated onboarding guide based on latest documentation
   * Updated with new requirements and clearer instructions
   */
  const renderOnboardingGuide = () => (
    <div>
      {/* Pre-requisites section with updated requirements */}
      <h3
        style={{
          fontSize: "20px",
          fontWeight: "bold",
          marginBottom: "16px",
          color: "#333",
          borderBottom: "2px solid #007bff",
          paddingBottom: "8px",
        }}
      >
        Before You Begin
      </h3>
      <p
        style={{
          fontSize: "16px",
          lineHeight: "1.7",
          color: "#555",
          marginBottom: "20px",
          textAlign: "justify",
        }}
      >
        Before you get started with the WhatsApp Business API (WABA) setup, make
        sure you have the following ready:
      </p>
      
      {/* Updated requirements list with checkmarks */}
      <div style={{ marginBottom: "24px" }}>
        {[
          "✅ Phone Number: Keep your business phone ready to receive the OTP. If you don't have one, you can get a free testing sandbox number from Facebook — just tap Login with Facebook and choose Get Free Number.",
          "✅ Remove Existing WhatsApp: If you're using WhatsApp on this number, back up your data and delete the account from your device.",
          "✅ Facebook Business Manager: Make sure you have access to your Facebook Business Manager.",
          "✅ 3Sigma Ultimate Plan: You must be on the 3Sigma Ultimate Plan to use the WhatsApp Business API.",
          "✅ Website & GST: A working website and valid GST number are mandatory for WhatsApp API approval.",
          "✅ Migration: Moving from another provider? Don't worry — your settings and templates will be migrated. Remove two-factor authentication (2FA) before setup.",
        ].map((item, index) => (
          <div
            key={index}
            style={{ 
              fontSize: "15px", 
              marginBottom: "12px", 
              color: "#333",
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              borderLeft: "4px solid #28a745",
              lineHeight: "1.6",
            }}
          >
            {item}
          </div>
        ))}
      </div>
      
      {/* Step-by-step process */}
      <h3
        style={{
          fontSize: "20px",
          fontWeight: "bold",
          marginBottom: "16px",
          color: "#333",
          borderBottom: "2px solid #007bff",
          paddingBottom: "8px",
        }}
      >
        Step-by-Step Process
      </h3>
      <ol style={{ paddingLeft: "0", margin: 0, counterReset: "step-counter" }}>
        {[
          "Sign Up for WABA API: Sign up for the WhatsApp Business API (WABA) using your Facebook Business Manager account.",
          "Permission Review: Grant the necessary permissions for the WABA API to manage your WhatsApp Business account.",
          "Business Information: Fill in your business details accurately. This information will be used to create your WhatsApp Business Account (WABA).",
          "Set Up Business Profile: Create your WhatsApp Business profile. This information will be visible to your customers when they contact you via WhatsApp.",
          "Verify Phone Number: Verify the phone number you want to use for your WhatsApp Business account. An OTP will be sent to this number.",
          "Connect to 3Sigma CRM: After verifying the number, authorize 3Sigma CRM to access your WABA account and complete the integration.",
          "Configure WABA in 3Sigma CRM: Once the setup is complete, configure your WhatsApp Business API settings within 3Sigma CRM to start managing your WhatsApp communications.",
        ].map((step, index) => (
          <li
            key={index}
            style={{ 
              fontSize: "15px", 
              marginBottom: "16px", 
              color: "#333",
              listStyle: "none",
              counterIncrement: "step-counter",
              padding: "16px",
              backgroundColor: "#ffffff",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
              position: "relative",
              paddingLeft: "60px",
              lineHeight: "1.6",
            }}
          >
            <span style={{
              position: "absolute",
              left: "16px",
              top: "16px",
              background: "#007bff",
              color: "white",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              fontWeight: "bold",
            }}>
              {index + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
      
      {/* Important note about integration time */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          background: "linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%)",
          borderRadius: "8px",
          border: "1px solid #fdcb6e",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ fontSize: "24px", marginRight: "12px" }}>⏱️</span>
          <strong style={{ fontSize: "16px", color: "#2d3436" }}>Important Timing Information</strong>
        </div>
        <p style={{
          fontSize: "15px",
          color: "#2d3436",
          margin: 0,
          lineHeight: "1.6",
        }}>
          The integration process may take <strong>15-20 minutes</strong> to complete. You
          can refresh the page to get the latest status. Please be patient during this process
          as Facebook verifies your account and sets up the API connection.
        </p>
      </div>
    </div>
  );

  /**
   * Renders the FAQ section with common questions and answers
   */
  const renderFAQ = () => (
    <div>
      <div style={{ marginBottom: "20px" }}>
        {[
          {
            question: "What is the WhatsApp Business API?",
            answer:
              "The WhatsApp Business API is designed for medium and large businesses to communicate with their customers at scale through WhatsApp. It allows businesses to send messages, notifications, and updates directly to users.",
          },
          {
            question: "How can I use this feature in 3Sigma CRM?",
            answer:
              "You can create and manage campaigns either from the lead screen or the campaigns menu. This allows you to select your audience, design messages, and track performance seamlessly.",
          },
          {
            question: "What types of messages can I send?",
            answer:
              "You can send promotional messages, updates, reminders, and any relevant information tailored to your leads.",
          },
          {
            question: "Are there any costs associated with using this feature?",
            answer:
              "Yes, using the WhatsApp Business API may incur costs based on the number of messages sent and the nature of the messages. Check with your account representative for detailed pricing.",
          },
          {
            question: "How do I ensure my messages reach my audience?",
            answer:
              "By using your official WhatsApp number and adhering to best practices for message frequency and content, you can ensure your campaigns are well-received.",
          },
          {
            question: "Can I get a free testing number?",
            answer:
              "Yes! If you don't have a business phone number ready, Facebook provides free testing sandbox numbers. Simply click 'Login with Facebook' and choose 'Get Free Number' option.",
          },
        ].map((item, index) => (
          <div 
            key={index} 
            style={{ 
              marginBottom: "20px",
              padding: "20px",
              backgroundColor: "#ffffff",
              border: "1px solid #e9ecef",
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
              borderLeft: "4px solid #007bff",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "12px",
                color: "#333",
                lineHeight: "1.4",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={{ 
                marginRight: "12px", 
                fontSize: "20px",
                color: "#007bff"
              }}>
                ❓
              </span>
              {item.question}
            </h3>
            <p
              style={{
                fontSize: "15px",
                lineHeight: "1.7",
                color: "#555",
                margin: 0,
                paddingLeft: "32px",
                textAlign: "justify",
              }}
            >
              {item.answer}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="facebook-integration-container">
      <div className="content-wrapper">
        <h1>Connect to WhatsApp Business API 🚀</h1>
        
        {/* Main CTA button with loading state */}
        <button
          className="facebook-login-button"
          onClick={launchWhatsAppSignup}
          disabled={isLoading}
        >
          <i className="ri-facebook-circle-fill"></i>
          {isLoading ? "Setting up..." : "Login with Facebook"}
        </button>
        
        {/* Free number option highlight */}
        <p style={{ 
          textAlign: "center", 
          fontSize: "14px", 
          color: "#666", 
          marginBottom: "24px",
          fontStyle: "italic",
          maxWidth: "500px",
          margin: "0 auto 24px auto"
        }}>
          💡 Don't have a business number? Get a free testing number during setup!
        </p>

        {/* Success message and refresh option */}
        {message ? (
          <>
            <h4 className="success-message">{message}</h4>
            <button
              className="facebook-login-button"
              style={{ margin: "20px auto" }}
              name="Refresh"
              onClick={onRefresh}
              disabled={isLoading}
            >
              {isLoading ? "Refreshing..." : "Refresh Status"}
            </button>
          </>
        ) : null}

        {/* Error message display */}
        {error && <p className="error-message">{error}</p>}
        
        {/* Information cards */}
        <div className="card-wrapper">
          <div className="card-container">
            {renderCard(
              "WhatsApp Business API Features and Benefits 🌟",
              renderWhatsAppAPIBenefits()
            )}
            {renderCard("Onboarding Guide 📜", renderOnboardingGuide())}
            {renderCard("Frequently Asked Questions ❓", renderFAQ())}
          </div>
        </div>
      </div>

      {/* Enhanced bottom navigation */}
      <div className="bottom-links">
        <div className="bottom-links-container">
          <div className="help-text">Need Help? 🤝</div>
          <a
            href="https://help.3sigmacrm.com/whatsapp-business-api/waba-api-onboarding"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="icon">📖</span>
            Complete Setup Guide
          </a>
          <a
            href="https://wa.me/918814048362"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="icon">💬</span>
            Live Support Chat
          </a>
          <a
            href="https://www.youtube.com/playlist?list=PL-Hf-swSRzhF2DZky4nDENZ2WeClSIrHD"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="icon">📹</span>
            Watch Video Demo
          </a>
        </div>
      </div>
      
      {/* Back to top button */}
      <button className="back-to-top" onClick={scrollToTop}>
        Back to Top ⬆
      </button>
      
      {/* Component styles */}
      <style>{`
        .facebook-integration-container {
          font-family: 'Gilroy', sans-serif;
          background-color: #f8f9fa;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          position: relative;
        }

        .content-wrapper {
          width: 100%;
          max-width: 1200px;
          margin-bottom: 140px;
          padding: 0 20px;
        }

        h1 {
          font-size: 30px;
          font-weight: bold;
          margin-bottom: 24px;
          color: #333;
          text-align: center;
          font-family: 'Gilroy', sans-serif;
        }

        .facebook-login-button {
          background-color: #1877f2;
          border: 0;
          border-radius: 8px;
          color: #fff;
          cursor: pointer;
          font-family: 'Gilroy', sans-serif;
          font-size: 16px;
          font-weight: bold;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          min-width: 200px;
          transition: background-color 0.3s ease;
        }

        .facebook-login-button:hover {
          background-color: #166fe5;
        }

        .facebook-login-button:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }

        .facebook-login-button i {
          font-size: 20px;
          margin-right: 8px;
        }

        .error-message {
          color: #dc3545;
          text-align: center;
          margin-bottom: 24px;
          font-family: 'Gilroy', sans-serif;
          background-color: #f8d7da;
          padding: 16px 20px;
          border-radius: 8px;
          border: 1px solid #f5c6cb;
          font-size: 15px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .success-message {
          font-family: 'Gilroy', sans-serif;
          text-align: center;
          margin-top: 24px;
          color: #155724;
          background-color: #d4edda;
          padding: 16px 20px;
          border-radius: 8px;
          border: 1px solid #c3e6cb;
          font-size: 15px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .card-wrapper {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .card-container {
          padding: 10px 10px 120px 10px; /* Added bottom padding for fixed footer */
          width: 100%;
          max-width: none;
        }

        .card {
          background-color: #ffffff;
          border-radius: 12px;
          padding: clamp(16px, 3vw, 24px);
          margin-bottom: 20px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          font-family: 'Gilroy', sans-serif;
          width: 100%;
          box-sizing: border-box;
        }

        .card-title {
          font-size: 22px;
          font-weight: bold;
          margin-bottom: 16px;
          color: #333;
          font-family: 'Gilroy', sans-serif;
        }

        .bottom-links {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 16px 20px;
          box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
          border-top: 3px solid #007bff;
          z-index: 1000;
        }

        .bottom-links-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: auto 1fr 1fr 1fr;
          gap: 20px;
          align-items: center;
        }

        .bottom-links-container .help-text {
          color: white;
          font-size: 16px;
          font-weight: 600;
          text-align: left;
          margin: 0;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
          white-space: nowrap;
        }

        .bottom-links a {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 14px 20px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: white;
          text-decoration: none;
          font-family: 'Gilroy', sans-serif;
          font-size: 15px;
          font-weight: 600;
          transition: all 0.3s ease;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
          min-height: 52px;
        }

        .bottom-links a:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          text-decoration: none;
        }

        .bottom-links a .icon {
          font-size: 20px;
          margin-right: 10px;
        }

        .back-to-top {
          position: fixed;
          bottom: 160px;
          right: 30px;
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          color: #ffffff;
          border: none;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          font-size: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(0, 123, 255, 0.3);
          z-index: 999;
          transition: all 0.3s ease;
        }

        .back-to-top:hover {
          background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
        }

        @media screen and (max-width: 600px) {
          .facebook-integration-container {
            padding: 12px;
          }
          
          .content-wrapper {
            padding: 0 4px;
            margin-bottom: 160px; /* More space for mobile footer */
          }
          
          .card {
            padding: 16px;
            margin-bottom: 16px;
          }
          
          .card-container {
            padding: 10px 10px 140px 10px;
          }
          
          .card-title {
            font-size: 18px;
          }
          
          h1 {
            font-size: 24px;
            margin-bottom: 16px;
          }
          
          .facebook-login-button {
            font-size: 14px;
            padding: 10px 20px;
            min-width: 180px;
          }
          
          .bottom-links {
            padding: 12px 16px;
          }
          
          .bottom-links-container {
            grid-template-columns: 1fr;
            gap: 8px;
            text-align: center;
          }
          
          .bottom-links a {
            font-size: 13px;
            padding: 10px 12px;
            min-height: 44px;
          }
          
          .help-text {
            font-size: 13px;
            margin-bottom: 6px;
          }
        }

        @media screen and (max-width: 480px) {
          .facebook-integration-container {
            padding: 8px;
          }
          
          .content-wrapper {
            margin-bottom: 180px;
          }
          
          .card {
            padding: 12px;
          }
          
          .card-container {
            padding: 8px 8px 160px 8px;
          }
          
          h1 {
            font-size: 20px;
          }
          
          .facebook-login-button {
            font-size: 13px;
            padding: 8px 16px;
            min-width: 160px;
          }
          
          .bottom-links {
            padding: 10px 12px;
          }
          
          .bottom-links a {
            font-size: 12px;
            padding: 8px 10px;
            min-height: 40px;
          }
          
          .help-text {
            font-size: 12px;
          }
          
          .back-to-top {
            bottom: 200px; /* More space for mobile footer */
            width: 48px;
            height: 48px;
            font-size: 18px;
          }
        }

        /* Ultra-wide screen support */
        @media screen and (min-width: 1400px) {
          .content-wrapper {
            max-width: 1400px;
          }
          
          .card-container {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            max-height: none;
          }
          
          .card:last-child {
            grid-column: 1 / -1;
          }
        }

        .back-to-top {
          position: fixed;
          bottom: 160px; /* Above the new prominent footer */
          right: 20px;
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          color: #ffffff;
          border: none;
          border-radius: 50%;
          width: 56px;
          height: 56px;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(0, 123, 255, 0.3);
          z-index: 999;
          transition: all 0.3s ease;
        }

        .back-to-top:hover {
          background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
        }
      `}</style>
    </div>
  );
};

export default FacebookIntegration;